import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { getTasks, getUsers } from '../utils/api';
import { formatDate } from '../utils/dateHelpers';
import TaskDrawer from '../components/TaskDrawer';

const Calendar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('agenda'); // month, week, day, agenda
  const [selectedTask, setSelectedTask] = useState(null);
  const [filters, setFilters] = useState({
    myTasks: false,
    department: '',
    priority: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const tasksRes = await getTasks();
      setTasks(tasksRes.data || []);
      
      try {
        const usersRes = await getUsers();
        setUsers(usersRes.data || []);
      } catch {
        setUsers([]);
      }
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      
      const taskDate = new Date(task.due_date);
      const isSameDay = 
        taskDate.getDate() === date.getDate() &&
        taskDate.getMonth() === date.getMonth() &&
        taskDate.getFullYear() === date.getFullYear();
      
      if (!isSameDay) return false;
      
      // Aplicar filtros
      if (filters.myTasks && task.responsible_user_id !== user?.id) return false;
      if (filters.department) {
        const taskUser = users.find(u => u.id === task.responsible_user_id);
        if (!taskUser || taskUser.department_name !== filters.department) return false;
      }
      if (filters.priority && task.priority !== filters.priority) return false;
      
      return true;
    });
  };

  const handleDateClick = (date) => {
    navigate(`/tasks/create?due_date=${date.toISOString().split('T')[0]}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pendiente':                        '#94A3B8',
      'En progreso':                      '#3B82F6',
      'Recurrente':                       '#06B6D4',
      'Bloqueada':                        '#EF4444',
      'En espera de cliente':             '#F59E0B',
      'En espera de usuario/Presidencia': '#F59E0B',
      'Lista para revisión':              '#8B5CF6',
      'Devuelta / Requiere ajustes':      '#F97316',
      'Aprobada / Cerrada':               '#10B981',
      'No realizada':                     '#EF4444',
    };
    return colors[status] || '#94A3B8';
  };

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const calendarDays = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(day);
    }

    const today = new Date();
    const isToday = (day) => {
      return day === today.getDate() &&
        month === today.getMonth() &&
        year === today.getFullYear();
    };

    return (
      <div className="decko-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border bg-secondary/30">
          {dayNames.map(day => (
            <div key={day} className="p-3 text-center text-sm font-bold text-foreground">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="border border-border bg-muted/20 min-h-[120px]" />;
            }

            const date = new Date(year, month, day);
            const dayTasks = getTasksForDate(date);
            const isTodayDate = isToday(day);

            return (
              <motion.div
                key={day}
                whileHover={{ scale: 1.02, backgroundColor: 'rgba(0,208,132,0.02)' }}
                onClick={() => handleDateClick(date)}
                className={`border border-border p-2 min-h-[120px] cursor-pointer transition-all ${
                  isTodayDate ? 'bg-primary/5 border-primary/30' : 'hover:bg-secondary/30'
                }`}
              >
                <div className={`text-sm font-bold mb-2 ${
                  isTodayDate ? 'text-primary' : 'text-foreground'
                }`}>
                  {day}
                </div>
                
                <div className="space-y-1">
                  {dayTasks.slice(0, 3).map(task => (
                    <motion.div
                      key={task.id}
                      whileHover={{ scale: 1.05, x: 2 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTask(task);
                      }}
                      className="text-[10px] px-2 py-1 rounded cursor-pointer truncate font-semibold text-white"
                      style={{
                        backgroundColor: getStatusColor(task.status),
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }}
                      title={task.title}
                    >
                      {task.title}
                    </motion.div>
                  ))}
                  {dayTasks.length > 3 && (
                    <div className="text-[10px] text-primary font-bold px-2">
                      +{dayTasks.length - 3} más
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    }

    return (
      <div className="decko-card overflow-hidden">
        <div className="grid grid-cols-7 gap-px bg-border">
          {weekDays.map((day, idx) => {
            const dayTasks = getTasksForDate(day);
            const isToday = day.toDateString() === new Date().toDateString();
            
            return (
              <div
                key={idx}
                className={`bg-card p-4 min-h-[400px] ${isToday ? 'bg-primary/5' : ''}`}
              >
                <div className={`text-center mb-3 ${isToday ? 'text-primary font-bold' : 'text-foreground'}`}>
                  <div className="text-xs">{dayNames[day.getDay()]}</div>
                  <div className="text-2xl font-bold">{day.getDate()}</div>
                </div>
                
                <div className="space-y-2">
                  {dayTasks.map(task => (
                    <motion.div
                      key={task.id}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setSelectedTask(task)}
                      className="p-2 rounded-lg cursor-pointer text-xs font-semibold text-white"
                      style={{ backgroundColor: getStatusColor(task.status) }}
                    >
                      <div className="truncate">{task.title}</div>
                      <div className="text-[10px] mt-1 opacity-80">{task.priority}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayTasks = getTasksForDate(currentDate);
    
    return (
      <div className="decko-card p-6">
        <div className="text-center mb-6">
          <div className="text-4xl font-bold text-primary mb-2">
            {currentDate.getDate()}
          </div>
          <div className="text-sm text-muted-foreground">
            {dayNames[currentDate.getDay()]}, {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </div>
        </div>
        
        {dayTasks.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-3">📭</div>
            <p className="text-muted-foreground mb-4">Sin tareas para este día</p>
            <button
              onClick={() => handleDateClick(currentDate)}
              className="btn-primary"
            >
              + Crear Tarea
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {dayTasks.map(task => (
              <motion.div
                key={task.id}
                whileHover={{ x: 4, scale: 1.01 }}
                onClick={() => setSelectedTask(task)}
                className="decko-card decko-card-hover p-4 cursor-pointer"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-foreground">{task.title}</h3>
                  <span
                    className="px-3 py-1 rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: getStatusColor(task.status) }}
                  >
                    {task.status}
                  </span>
                </div>
                {task.description && (
                  <p className="text-sm text-muted-foreground mb-2">{task.description}</p>
                )}
                <div className="flex gap-2">
                  <span className="text-xs font-semibold text-primary">{task.priority}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderAgendaView = () => {
    const today = new Date();
    const agendaDays = [];
    for (let i = 0; i < 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayTasks = getTasksForDate(date);
      if (dayTasks.length > 0) agendaDays.push({ date, tasks: dayTasks });
    }

    const noDateTasks = tasks.filter(t => {
      if (t.due_date) return false;
      if (!['Pendiente','En progreso','Recurrente','Bloqueada','En espera de cliente','En espera de usuario/Presidencia','Lista para revisión','Devuelta / Requiere ajustes'].includes(t.status)) return false;
      if (filters.myTasks && t.responsible_user_id !== user?.id) return false;
      return true;
    });

    const TaskRow = ({ task }) => (
      <motion.div whileHover={{ x: 4 }} onClick={() => setSelectedTask(task)}
        className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all hover:bg-secondary">
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: getStatusColor(task.status) }} />
        <div className="flex-1 min-w-0">
          {task.responsabilidad && (
            <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: '#CBD5E1' }}>{task.responsabilidad}</p>
          )}
          <p className="font-semibold text-foreground text-sm truncate">{task.title}</p>
          <p className="text-xs text-muted-foreground">{task.status} · {task.priority}</p>
        </div>
      </motion.div>
    );

    return (
      <div className="space-y-4">
        {/* Con fecha */}
        {agendaDays.map(({ date, tasks: dayTasks }, idx) => (
          <div key={idx} className="decko-card overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-3"
              style={{ background: 'rgba(0,208,132,0.04)' }}>
              <span className="text-sm font-black" style={{ color: '#00D084' }}>{date.getDate()}</span>
              <div>
                <p className="text-xs font-bold text-foreground">{dayNames[date.getDay()]}, {monthNames[date.getMonth()]}</p>
              </div>
              <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                {dayTasks.length} tarea{dayTasks.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="p-2">
              {dayTasks.map(task => <TaskRow key={task.id} task={task} />)}
            </div>
          </div>
        ))}

        {/* Sin fecha */}
        {noDateTasks.length > 0 && (
          <div className="decko-card overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-3"
              style={{ background: 'rgba(148,163,184,0.06)' }}>
              <span className="text-sm">📋</span>
              <p className="text-xs font-bold text-foreground">Sin fecha límite</p>
              <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                {noDateTasks.length}
              </span>
            </div>
            <div className="p-2">
              {noDateTasks.map(task => <TaskRow key={task.id} task={task} />)}
            </div>
          </div>
        )}

        {agendaDays.length === 0 && noDateTasks.length === 0 && (
          <div className="decko-card text-center py-12">
            <p className="text-4xl mb-3">🎉</p>
            <p className="text-muted-foreground">Sin tareas activas</p>
          </div>
        )}
      </div>
    );
  };

  const departments = [...new Set(users.map(u => u.department_name))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="px-6 py-5" style={{
        background: 'rgba(10, 26, 20, 0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: '2px solid rgba(0, 208, 132, 0.3)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(0,208,132,0.1)'
      }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-black" style={{
              background: 'linear-gradient(135deg, #00D084 0%, #00FFB3 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Calendario
            </h1>
            <p className="text-sm font-semibold mt-1" style={{ color: '#A0F0D0' }}>
              📅 {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
              className="btn-secondary px-3 py-2"
            >
              ←
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="btn-secondary px-3 py-2 text-xs"
            >
              Hoy
            </button>
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
              className="btn-secondary px-3 py-2"
            >
              →
            </button>
          </div>
        </div>

        {/* Vista Toggle + Filtros */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {['month', 'week', 'day', 'agenda'].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  view === v
                    ? 'bg-primary text-white'
                    : 'bg-white/5 text-[#A0F0D0] hover:bg-white/10'
                }`}
              >
                {v === 'month' && '📅 Mes'}
                {v === 'week' && '📆 Semana'}
                {v === 'day' && '📋 Día'}
                {v === 'agenda' && '📝 Agenda'}
              </button>
            ))}
          </div>
          
          <div className="flex gap-2">
            <label className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={filters.myTasks}
                onChange={(e) => setFilters({...filters, myTasks: e.target.checked})}
                className="w-4 h-4"
                style={{ accentColor: '#00D084' }}
              />
              <span className="text-xs font-semibold" style={{ color: '#A0F0D0' }}>Mis Tareas</span>
            </label>
          </div>
        </div>
      </div>

      <div className="p-6">
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
        {view === 'day' && renderDayView()}
        {view === 'agenda' && renderAgendaView()}
      </div>

      {selectedTask && (
        <TaskDrawer
          task={selectedTask}
          users={users}
          onClose={() => setSelectedTask(null)}
          onUpdate={loadData}
        />
      )}
    </div>
  );
};

export default Calendar;
